import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ChatSession, ChatMessage, MemberProposal, SessionType } from '@/types';
import { api } from '@/lib/api';
import { useAuthStore } from './useAuthStore';

interface ChatStore {
  sessions: ChatSession[];
  activeSessionId: string | null;

  // Session management
  createSession: (type: SessionType, memberIds?: string[]) => ChatSession;
  setActiveSession: (id: string | null) => void;
  deleteSession: (id: string) => void;
  updateSessionTitle: (id: string, title: string) => void;
  addMemberToSession: (sessionId: string, memberId: string) => void;

  // Message actions (operate on active session)
  appendMessage: (message: ChatMessage) => void;
  updateLastAssistantMessage: (content: string) => void;
  setMessageProposal: (messageId: string, proposal: MemberProposal) => void;

  // Local state management
  clearLocal: () => void;
}

function isLoggedIn(): boolean {
  return useAuthStore.getState().user !== null;
}

export const useChatStore = create<ChatStore>()(
  persist(
    (set, get) => ({
      sessions: [],
      activeSessionId: null,

      createSession: (type, memberIds?) => {
        const session: ChatSession = {
          id: crypto.randomUUID(),
          type,
          title: '',
          memberIds: memberIds ?? [],
          messages: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        set((state) => ({ sessions: [session, ...state.sessions] }));
        if (isLoggedIn()) {
          api.sessions.create({ type, id: session.id, memberIds }).catch(console.error);
        }
        return session;
      },

      setActiveSession: (id) => set({ activeSessionId: id }),

      deleteSession: (id) => {
        set((state) => ({
          sessions: state.sessions.filter((s) => s.id !== id),
          activeSessionId: state.activeSessionId === id ? null : state.activeSessionId,
        }));
        if (isLoggedIn()) {
          api.sessions.delete(id).catch(console.error);
        }
      },

      updateSessionTitle: (id, title) => {
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === id ? { ...s, title, updatedAt: new Date().toISOString() } : s,
          ),
        }));
        if (isLoggedIn()) {
          api.sessions.patch(id, { title }).catch(console.error);
        }
      },

      addMemberToSession: (sessionId, memberId) => {
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId && !s.memberIds.includes(memberId)
              ? { ...s, memberIds: [...s.memberIds, memberId] }
              : s,
          ),
        }));
        if (isLoggedIn()) {
          api.sessions.patch(sessionId, { addMemberId: memberId }).catch(console.error);
        }
      },

      appendMessage: (message) => {
        const { activeSessionId } = get();
        if (!activeSessionId) return;
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === activeSessionId
              ? { ...s, messages: [...s.messages, message], updatedAt: new Date().toISOString() }
              : s,
          ),
        }));
        // Only sync completed messages (not streaming stubs)
        if (isLoggedIn() && message.content) {
          api.sessions
            .appendMessage(activeSessionId, {
              role: message.role,
              content: message.content,
              timestamp: message.timestamp,
            })
            .catch(console.error);
        }
      },

      updateLastAssistantMessage: (content) => {
        const { activeSessionId } = get();
        if (!activeSessionId) return;
        set((state) => ({
          sessions: state.sessions.map((s) => {
            if (s.id !== activeSessionId) return s;
            const messages = [...s.messages];
            for (let i = messages.length - 1; i >= 0; i--) {
              if (messages[i].role === 'assistant') {
                messages[i] = { ...messages[i], content };
                break;
              }
            }
            return { ...s, messages };
          }),
        }));
      },

      setMessageProposal: (messageId, proposal) => {
        const { activeSessionId } = get();
        if (!activeSessionId) return;
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === activeSessionId
              ? {
                  ...s,
                  messages: s.messages.map((m) =>
                    m.id === messageId ? { ...m, memberProposal: proposal } : m,
                  ),
                }
              : s,
          ),
        }));
      },

      clearLocal: () => set({ sessions: [], activeSessionId: null }),
    }),
    {
      name: 'inner-team-chat-v1',
      partialize: (state) => ({
        sessions: state.sessions,
        activeSessionId: state.activeSessionId,
      }),
    },
  ),
);
